import {
  Activity,
  Bot,
  ChevronDown,
  Cpu,
  Mic,
  RefreshCw,
  Server,
  Trash2,
  Volume2,
  Wifi,
  WifiOff,
} from 'lucide-react'
import { emotionLabelEntry } from '../../../../utils/emotion'

function formatAge(ms) {
  if (!ms || ms < 0) return '-'
  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m${seconds % 60}s`
  return `${Math.floor(minutes / 60)}h${minutes % 60}m`
}

function TelemetryCell({ icon: Icon, label, value, detail, active = false, warning = false, title }) {
  const tone = warning
    ? 'border-amber-300/20 bg-amber-300/10 text-amber-100'
    : active
      ? 'border-cyan-300/20 bg-cyan-300/10 text-cyan-100'
      : 'border-white/10 bg-white/[0.055] text-slate-200'

  return (
    <div className={`min-w-0 rounded-2xl border px-3.5 py-3 ${tone}`} title={title || ''}>
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-white/45">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        <span>{label}</span>
      </div>
      <div className="mt-1.5 break-words text-sm font-bold leading-snug text-white">{value}</div>
      {detail ? <div className="mt-1 text-[10px] font-semibold text-white/45">{detail}</div> : null}
    </div>
  )
}

export function MonitorStatus({
  clearMessages,
  connect,
  connecting,
  controlHint,
  debugEvents,
  debugExiting,
  debugPresence,
  detailsExiting,
  detailsPresence,
  deviceId,
  deviceMeta,
  deviceOnline,
  effectiveChatMode,
  emotion,
  emotionEmoji,
  isPublicTest,
  isTyping,
  nowTick,
  pipeline,
  primaryReady,
  retryIn,
  setShowDebug,
  setShowDetails,
  setTargetDeviceId,
  showDebug,
  showDetails,
  targetDeviceId,
  tx,
  wsConnected,
  wsSessionId,
  wsUrl,
}) {
  const primaryLabel = primaryReady
    ? isPublicTest
      ? tx({ zh: '云端已连接', en: 'Cloud connected' })
      : tx({ zh: '设备可控', en: 'Device controllable' })
    : isPublicTest
      ? tx({ zh: '云端未连接', en: 'Cloud disconnected' })
      : tx({ zh: '设备不可控', en: 'Device not controllable' })

  return (
    <section className="relative overflow-hidden rounded-[30px] border border-slate-800 bg-slate-950 text-white shadow-[0_24px_80px_-36px_rgba(15,23,42,0.75)]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(250,204,21,0.17),transparent_34%),radial-gradient(circle_at_82%_0%,rgba(34,211,238,0.13),transparent_28%)]" />
      <div className="relative p-4 sm:p-6">
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(400px,0.88fr)] xl:items-start">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
              <Activity className="h-3.5 w-3.5" />
              {tx({ zh: '实时设备指挥台', en: 'Live device command center' })}
            </div>
            <div className="mt-3 flex items-start gap-3">
              <span className={`mt-2 h-2.5 w-2.5 shrink-0 rounded-full ${primaryReady ? 'bg-emerald-400 shadow-[0_0_18px_rgba(52,211,153,0.85)]' : 'bg-rose-400 shadow-[0_0_18px_rgba(251,113,133,0.65)]'}`} />
              <div className="min-w-0">
                <h1 className="text-xl font-black tracking-tight sm:text-2xl">{primaryLabel}</h1>
                <p className="mt-1 max-w-2xl text-sm font-medium leading-relaxed text-slate-300">
                  {controlHint}
                  {!wsConnected && retryIn > 0 ? tx({ zh: '（{n}s 后自动重试）', en: ' (automatic retry in {n}s)' }, { n: retryIn }) : ''}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.055] p-3.5 backdrop-blur">
            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/45" htmlFor="monitor-device-id">
              {isPublicTest ? tx({ zh: '目标设备（可空）', en: 'Target device (optional)' }) : tx({ zh: '目标设备 ID', en: 'Target device ID' })}
            </label>
            <div className="mt-2 flex min-w-0 flex-col gap-2 sm:flex-row">
              <input
                id="monitor-device-id"
                value={targetDeviceId}
                onChange={(event) => setTargetDeviceId(event.target.value)}
                placeholder={isPublicTest ? 'public-test' : '10:20:ba:…'}
                className="min-w-0 flex-1 rounded-xl border border-white/10 bg-black/20 px-3.5 py-2.5 text-sm font-semibold text-white outline-none placeholder:text-white/30 focus:border-amber-300 focus:ring-2 focus:ring-amber-300/15"
              />
              <div className="grid grid-cols-2 gap-2 sm:flex">
                <button
                  type="button"
                  onClick={clearMessages}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 px-3 py-2.5 text-xs font-bold text-slate-200 transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/30"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {tx({ zh: '清空', en: 'Clear' })}
                </button>
                <button
                  type="button"
                  onClick={() => connect()}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-300 px-3.5 py-2.5 text-xs font-black text-slate-950 transition hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-200 disabled:opacity-60"
                  disabled={connecting}
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${connecting ? 'animate-spin' : ''}`} />
                  {connecting ? tx({ zh: '连接中', en: 'Connecting' }) : tx({ zh: '重新连接', en: 'Reconnect' })}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className={`mt-5 grid grid-cols-1 gap-2 min-[390px]:grid-cols-2 ${isPublicTest ? 'lg:grid-cols-3' : 'lg:grid-cols-5'}`}>
          {!isPublicTest && (
            <TelemetryCell
              icon={Mic}
              label="ASR"
              value={pipeline.lastStt?.text
                ? tx({ zh: '已识别 · {age}', en: 'Recognized · {age}' }, { age: formatAge(nowTick - pipeline.lastStt.at) })
                : tx({ zh: '等待输入', en: 'Waiting for input' })}
              active={Boolean(pipeline.lastStt?.text)}
              title={pipeline.lastStt?.text || ''}
            />
          )}
          <TelemetryCell
            icon={Bot}
            label="LLM"
            value={isTyping ? tx({ zh: '正在生成', en: 'Generating' }) : tx({ zh: '空闲', en: 'Idle' })}
            active={isTyping}
            warning={isTyping}
            title={pipeline.lastAssistantProvider !== '-' ? `provider=${pipeline.lastAssistantProvider}` : ''}
          />
          {!isPublicTest && (
            <TelemetryCell
              icon={Volume2}
              label={tx({ zh: 'TTS / 播放', en: 'TTS / Playback' })}
              value={pipeline.lastTts?.state
                ? pipeline.lastTts.state === 'stop'
                  ? tx({ zh: '已结束', en: 'Stopped' })
                  : pipeline.lastTts.state
                : effectiveChatMode === 'cloud'
                  ? tx({ zh: '云端模式', en: 'Cloud mode' })
                  : tx({ zh: '等待设备', en: 'Waiting for device' })}
              active={Boolean(pipeline.lastTts?.state && pipeline.lastTts.state !== 'stop')}
              title={pipeline.lastTts?.text || ''}
            />
          )}
          <TelemetryCell
            icon={Activity}
            label={tx({ zh: '当前情绪', en: 'Emotion' })}
            value={`${emotionEmoji} ${tx(emotionLabelEntry(emotion))}`}
            detail={pipeline.lastEmotionAt ? tx({ zh: '{age} 前更新', en: 'Updated {age} ago' }, { age: formatAge(nowTick - pipeline.lastEmotionAt) }) : ''}
            active={Boolean(pipeline.lastEmotionAt)}
          />
          {!isPublicTest && (
            <TelemetryCell
              icon={Cpu}
              label={tx({ zh: '设备固件', en: 'Device firmware' })}
              value={deviceMeta.version !== '-' || deviceMeta.board !== '-'
                ? `${deviceMeta.board} · v${deviceMeta.version}`
                : tx({ zh: '暂未上报', en: 'Not reported' })}
              detail={deviceMeta.updatedAt ? tx({ zh: '{age} 前更新', en: 'Updated {age} ago' }, { age: formatAge(nowTick - deviceMeta.updatedAt) }) : ''}
              active={Boolean(deviceMeta.updatedAt)}
            />
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-white/10 pt-4 text-xs font-semibold text-slate-300">
          <button
            type="button"
            onClick={() => setShowDetails((value) => !value)}
            aria-expanded={showDetails}
            aria-controls="monitor-connection-details"
            className="inline-flex items-center gap-2 rounded-lg py-1 text-left transition hover:text-white focus:outline-none focus:ring-2 focus:ring-white/25"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
            {showDetails ? tx({ zh: '收起连接详情', en: 'Hide connection details' }) : tx({ zh: '查看连接详情', en: 'View connection details' })}
          </button>
          <button
            type="button"
            onClick={() => setShowDebug((value) => !value)}
            aria-expanded={showDebug}
            aria-controls="monitor-ws-log"
            className="inline-flex items-center gap-2 rounded-lg py-1 text-left transition hover:text-white focus:outline-none focus:ring-2 focus:ring-white/25"
          >
            <Server className="h-4 w-4" />
            {showDebug ? tx({ zh: '收起原始 WS', en: 'Hide raw WS' }) : tx({ zh: '原始 WS 日志', en: 'Raw WS log' })}
          </button>
        </div>

        {detailsPresence.mounted && (
          <div id="monitor-connection-details" className={`mt-3 grid gap-2 rounded-2xl border border-white/10 bg-black/15 p-3 text-xs text-slate-300 sm:grid-cols-2 ${detailsExiting ? 'chamii-reveal-exit pointer-events-none' : 'chamii-reveal-enter'}`}>
            <div className="flex min-w-0 items-center gap-2" title={wsUrl || ''}>
              {wsConnected ? <Wifi className="h-4 w-4 shrink-0 text-emerald-400" /> : <WifiOff className="h-4 w-4 shrink-0 text-rose-400" />}
              <span className="truncate">{tx({ zh: '控制台 ↔ 云端：', en: 'Console ↔ Cloud: ' })}{wsConnected ? tx({ zh: '已连接', en: 'Connected' }) : connecting ? tx({ zh: '连接中', en: 'Connecting' }) : tx({ zh: '未连接', en: 'Disconnected' })}{wsSessionId ? ` · ${wsSessionId}` : ''}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className={`h-2 w-2 shrink-0 rounded-full ${deviceOnline ? 'bg-emerald-400' : 'bg-rose-400'}`} />
              <span className="truncate">{tx({ zh: '设备 ↔ 云端：', en: 'Device ↔ Cloud: ' })}{deviceOnline ? tx({ zh: '在线', en: 'Online' }) : tx({ zh: '离线', en: 'Offline' })} · {deviceId}</span>
            </div>
          </div>
        )}

        {debugPresence.mounted && (
          <div id="monitor-ws-log" className={`mt-3 max-h-[180px] overflow-y-auto rounded-2xl border border-white/10 bg-black/25 p-3 ${debugExiting ? 'chamii-reveal-exit pointer-events-none' : 'chamii-reveal-enter'}`}>
            <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-white/40">
              {tx({ zh: '最近 {n} 条事件', en: 'Latest {n} events' }, { n: debugEvents.length })}
            </div>
            <pre className="whitespace-pre-wrap text-[11px] leading-relaxed text-slate-300">{debugEvents.map((event) => `[${new Date(event.at).toLocaleTimeString()}] ${event.type} ${event.brief}`).join('\n')}</pre>
          </div>
        )}
      </div>
    </section>
  )
}
